"use client";

import { motion, useReducedMotion } from "framer-motion";
import { Menu, Search, ShoppingCart, User, X } from "lucide-react";
import type { Route } from "next";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { LogoutMenuItem } from "@/components/auth/logout-button";
import { NavbarSearchInput } from "@/components/common/navbar-search-input";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { useAuth } from "@/hooks/use-auth";
import {
  useAuthEnabled,
  useCartEnabled,
  useGuestCheckoutMode,
} from "@/hooks/use-feature-flags";
import { buildAccountUrl, routes } from "@/utils/routes";

// Sophisticated animated burger menu icon component
const AnimatedMenuIcon = ({ isOpen }: { isOpen: boolean }) => {
  const shouldReduceMotion = useReducedMotion();

  // Premium animation variants for burger-to-X transformation
  const topLineVariants = {
    closed: {
      rotate: 0,
      y: 0,
      transition: {
        duration: 0.3,
        ease: [0.25, 0.46, 0.45, 0.94] as const,
      },
    },
    open: {
      rotate: 45,
      y: 6,
      transition: {
        duration: 0.3,
        ease: [0.25, 0.46, 0.45, 0.94] as const,
      },
    },
  };

  const middleLineVariants = {
    closed: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.2,
        ease: [0.25, 0.46, 0.45, 0.94] as const,
      },
    },
    open: {
      opacity: 0,
      x: -10,
      transition: {
        duration: 0.2,
        ease: [0.25, 0.46, 0.45, 0.94] as const,
      },
    },
  };

  const bottomLineVariants = {
    closed: {
      rotate: 0,
      y: 0,
      transition: {
        duration: 0.3,
        ease: [0.25, 0.46, 0.45, 0.94] as const,
      },
    },
    open: {
      rotate: -45,
      y: -6,
      transition: {
        duration: 0.3,
        ease: [0.25, 0.46, 0.45, 0.94] as const,
      },
    },
  };

  if (shouldReduceMotion) {
    return isOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />;
  }

  return (
    <div className="relative h-5 w-5">
      <svg
        aria-hidden="true"
        className="h-full w-full"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        viewBox="0 0 24 24"
      >
        <motion.line
          animate={isOpen ? "open" : "closed"}
          initial="closed"
          variants={topLineVariants}
          x1="3"
          x2="21"
          y1="6"
          y2="6"
        />
        <motion.line
          animate={isOpen ? "open" : "closed"}
          initial="closed"
          variants={middleLineVariants}
          x1="3"
          x2="21"
          y1="12"
          y2="12"
        />
        <motion.line
          animate={isOpen ? "open" : "closed"}
          initial="closed"
          variants={bottomLineVariants}
          x1="3"
          x2="21"
          y1="18"
          y2="18"
        />
      </svg>
    </div>
  );
};

export default function HeaderActions({
  isPending,
  basketCount,
  isSearchOpen,
  setIsSearchOpen,
  isMenuOpen,
  setIsMenuOpen,
}: {
  isPending: boolean;
  basketCount: number;
  isSearchOpen: boolean;
  setIsSearchOpen: (v: boolean) => void;
  isMenuOpen: boolean;
  setIsMenuOpen: (v: boolean) => void;
}) {
  const router = useRouter();
  const { isAuthenticated, isLoading, fullName } = useAuth();
  const isAuthEnabled = useAuthEnabled();
  const isCartEnabled = useCartEnabled();
  const isGuestCheckoutMode = useGuestCheckoutMode();

  return (
    <div className="flex items-center space-x-3">
      <div className="hidden items-center space-x-3 lg:flex">
        <div className="relative w-72">
          <NavbarSearchInput />
        </div>
      </div>

      <Sheet onOpenChange={setIsSearchOpen} open={isSearchOpen}>
        <SheetTrigger asChild>
          <Button
            aria-label="Search products"
            className="h-10 w-10 lg:hidden"
            size="icon"
            variant="ghost"
          >
            <Search className="h-5 w-5" />
          </Button>
        </SheetTrigger>
        <SheetContent className="h-auto border-none shadow-lg" side="top">
          <div className="bg-background px-6 py-10">
            <SheetHeader className="mb-6 px-0">
              <SheetTitle className="text-left font-bold font-heading text-xl tracking-tight">
                Search Products
              </SheetTitle>
            </SheetHeader>

            <div className="space-y-6">
              <div className="relative">
                <NavbarSearchInput />
              </div>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Cart Button - Only show when cart is enabled */}
      {isCartEnabled && (
        <Button
          aria-label="View basket"
          className="relative h-10 w-10"
          disabled={isPending}
          onClick={() => router.push("/basket")}
          size="icon"
          variant="ghost"
        >
          <ShoppingCart
            className={`h-5 w-5 ${isPending ? "animate-pulse" : ""}`}
          />
          {basketCount > 0 && (
            <span
              aria-live="polite"
              className={`-top-1.5 -right-1.5 absolute flex h-5 w-5 items-center justify-center rounded-full bg-primary font-semibold text-primary-foreground text-xs transition-all duration-200 ${isPending ? "scale-110 bg-primary/80" : "scale-100"}`}
            >
              {basketCount}
            </span>
          )}
          {isPending && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="h-2 w-2 animate-spin rounded-full border border-primary border-t-transparent" />
            </div>
          )}
        </Button>
      )}

      {/* User Menu - Only show when auth is enabled and not in guest checkout mode */}
      {isAuthEnabled && !isGuestCheckoutMode && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              aria-label="User account menu"
              className="h-10 w-10"
              size="icon"
              variant="ghost"
            >
              <User className="h-5 w-5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-60">
            {isLoading && (
              <div className="flex items-center justify-center py-4">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              </div>
            )}

            {!isLoading && isAuthenticated && (
              <>
                {/* Authenticated User Menu */}
                {fullName && (
                  <div className="px-3 py-2 font-medium text-foreground text-sm">
                    {fullName}
                  </div>
                )}
                {fullName && <DropdownMenuSeparator />}
                <DropdownMenuItem>
                  <Link
                    className="block w-full px-3 py-2"
                    href={buildAccountUrl("profile") as Route}
                  >
                    My Account
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem>
                  <Link
                    className="block w-full px-3 py-2"
                    href={buildAccountUrl("orders") as Route}
                  >
                    Order History
                  </Link>
                </DropdownMenuItem>
                {/* Only show wishlist when commerce is enabled */}
                {isCartEnabled && (
                  <DropdownMenuItem>
                    <Link
                      className="block w-full px-3 py-2"
                      href={buildAccountUrl("wishlist") as Route}
                    >
                      Wishlist
                    </Link>
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <LogoutMenuItem />
              </>
            )}

            {!(isLoading || isAuthenticated) && (
              <>
                {/* Unauthenticated User Menu */}
                <DropdownMenuItem>
                  <Link
                    className="block w-full px-3 py-2"
                    href={routes.auth.login as Route}
                  >
                    Sign In
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem>
                  <Link
                    className="block w-full px-3 py-2"
                    href={routes.auth.register as Route}
                  >
                    Create Account
                  </Link>
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      <Button
        aria-controls="mobile-menu"
        aria-expanded={isMenuOpen}
        aria-label={isMenuOpen ? "Close menu" : "Open menu"}
        className="h-10 w-10 lg:hidden"
        onClick={() => setIsMenuOpen(!isMenuOpen)}
        size="icon"
        variant="ghost"
      >
        <AnimatedMenuIcon isOpen={isMenuOpen} />
      </Button>
    </div>
  );
}
